import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { SEO_PATH_BLOCKS, classifyLandingPath } from '@/components/seo/landingBlocks';
import type { SeoReport } from '@/components/seo/types';
import { useGa4WebAudit } from '@/hooks/useGa4WebAudit';
import { useSeoAudit } from '@/hooks/useSeoAudit';
import { fmt } from '@/components/seo/format';

function aggregateGscByPath(report: SeoReport | undefined): Map<string, { clicks: number; impressions: number }> {
    const map = new Map<string, { clicks: number; impressions: number }>();
    if (!report) return map;
    for (const block of report.blocks) {
        for (const row of block.rows) {
            const path = row.path;
            if (!path) continue;
            const after = row.after;
            const clicks = after?.clicks ?? 0;
            const impressions = after?.impressions ?? 0;
            const prev = map.get(path) ?? { clicks: 0, impressions: 0 };
            prev.clicks += clicks;
            prev.impressions += impressions;
            map.set(path, prev);
        }
    }
    return map;
}

export function WebBridgePanel({ days }: { days: number }) {
    const { data: ga4, isLoading: ga4Loading } = useGa4WebAudit(days);
    const { data: seo, isLoading: seoLoading } = useSeoAudit(12, days);

    const rows = useMemo(() => {
        if (!ga4?.pages?.length) return [];

        const gscByPath = aggregateGscByPath(seo);

        const blocks = SEO_PATH_BLOCKS.map((block) => ({
            id: block.id,
            name: block.name,
            gscClicks: 0,
            gscImpressions: 0,
            ga4Users: 0,
            ga4PageViews: 0,
        }));

        const index = new Map(blocks.map((b) => [b.id, b]));

        for (const page of ga4.pages) {
            const blockId = classifyLandingPath(page.path) ?? 'other';
            const bucket = index.get(blockId);
            if (!bucket) continue;
            bucket.ga4Users += page.active_users;
            bucket.ga4PageViews += page.screen_page_views;
            const gsc = gscByPath.get(page.path);
            if (gsc) {
                bucket.gscClicks += gsc.clicks;
                bucket.gscImpressions += gsc.impressions;
            }
        }

        for (const [path, metric] of gscByPath) {
            const blockId = classifyLandingPath(path) ?? 'other';
            const bucket = index.get(blockId);
            if (!bucket) continue;
            if (!ga4.pages.some((p) => p.path === path)) {
                bucket.gscClicks += metric.clicks;
                bucket.gscImpressions += metric.impressions;
            }
        }

        return blocks
            .filter((b) => b.ga4Users > 0 || b.gscClicks > 0 || b.gscImpressions > 0)
            .sort((a, b) => b.ga4Users - a.ga4Users);
    }, [ga4, seo]);

    if (ga4Loading || seoLoading) {
        return <Loader2 className="animate-spin text-muted-foreground" size={18} />;
    }

    const seoReady = Boolean(seo?.weeks?.length);

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Cruce por bloques del mapa SEO ({SEO_PATH_BLOCKS.length} clusters).
                {!seoReady && ' Search Console aún sin caché: solo columnas GA4 hasta el cron seo-gsc-sync.'}
            </p>
            <div className="overflow-auto rounded-xl border border-border">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border bg-muted/30">
                            <th className="px-3 py-2 text-left font-medium">Bloque</th>
                            <th className="px-3 py-2 text-right font-medium">Clics GSC</th>
                            <th className="px-3 py-2 text-right font-medium">Impres. GSC</th>
                            <th className="px-3 py-2 text-right font-medium">Usuarios GA4</th>
                            <th className="px-3 py-2 text-right font-medium">Page views</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                                    Sin datos agregados en esta ventana.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.id} className="border-t border-border">
                                    <td className="px-3 py-2 font-medium">{row.name}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row.gscClicks, 0)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row.gscImpressions, 0)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row.ga4Users, 0)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row.ga4PageViews, 0)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-muted-foreground">
                Licencias: bloque «Licencias de pesca». Embudo en pestaña Embudos.
            </p>
        </div>
    );
}
